// piano-frontend/src/pages/admin/OperationsBasicHours.js
// 운영 설정 — 운영 시간 설정(기본) 탭 (PC 전용).
//  - 레슨: 좌측 정밀 입력 + 우측 주간 시간표 뷰. 시간표의 운영 블록 가장자리를
//    드래그해 30분 단위로 시간을 조절한다 (평일 5일은 한 묶음으로 함께 움직임).
//  - 연습실: 주간 개요 바 + 시간×방 통합 가용성 그리드(클릭/드래그 페인팅).
// 변경 사항은 더티 추적되어 하단 고정 저장 바에서 저장/되돌리기 한다.
// 기존 API(PUT /lesson-config, PUT /practice-config)를 그대로 사용하며,
// 연습실 저장의 예약 충돌(409)은 목록 확인 후 '무시하고 저장'으로 이어진다.
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, Stack, Paper, Button, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Tabs, Tab, Tooltip
} from "@mui/material";
import { KeyboardArrowUp } from "@mui/icons-material";
import api from "../../api/axios";
import { useDarkMode } from "../../App";
import { useDialog } from "../../contexts/DialogProvider";

const TIME_OPTIONS = (() => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return options;
})();

const DAYS = [
  ["monday", "월"], ["tuesday", "화"], ["wednesday", "수"],
  ["thursday", "목"], ["friday", "금"], ["saturday", "토"], ["sunday", "일"]
];

// 레슨 요일(월~토, 일요일은 휴무 고정) + 요일별 기본값 (서버 백필 값과 동일)
const LESSON_DAYS = DAYS.slice(0, 6);
const LESSON_DAY_DEFAULTS = {
  monday: ["14:00", "22:00"], tuesday: ["14:00", "22:00"], wednesday: ["14:00", "22:00"],
  thursday: ["14:00", "22:00"], friday: ["14:00", "18:00"], saturday: ["12:00", "18:00"]
};

const DEFAULT_ROOMS = ["Lesson 1", "Lesson 2", "Lesson 3", "Lesson 4", "Lesson 5", "Lesson 6"];

// 레슨 주간 시간표 축: 기본 10:00 시작 ~ 23:30, 30분 = SLOT_H px.
// 10:00 이전은 '이전 시간 보기'로 확장하며, 더 이른 운영 시간이 설정돼 있으면 자동으로 내려간다.
const AXIS_MIN = 0;
const AXIS_DEFAULT_START = 10 * 60;
const AXIS_END = 23 * 60 + 30;
const SLOT_H = 20;

const hhmm = (t) => (t ? String(t).slice(0, 5) : "");
const toMin = (t) => {
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  return h * 60 + m;
};
const minToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function generateTimeSlots(start, end) {
  if (!start || !end) return [];
  const slots = [];
  let m = toMin(start);
  const endM = toMin(end);
  while (m < endM) {
    slots.push(minToTime(m));
    m += 30;
  }
  return slots;
}

function TimeSelect({ label, value, onChange, width = 108 }) {
  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value || ""} label={label} onChange={(e) => onChange(e.target.value)} notched>
        {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

// 더티 상태 하단 고정 저장 바
function SaveBar({ dirty, saving, onSave, onReset }) {
  if (!dirty) return null;
  return (
    <Box sx={{
      position: "sticky", bottom: 0, mt: 2, mx: -2, px: 2, py: 1,
      bgcolor: "background.paper", borderTop: 1, borderColor: "divider",
      display: "flex", alignItems: "center", gap: 1, zIndex: 5
    }}>
      <Typography variant="caption" color="warning.main" fontWeight={600}>
        저장되지 않은 변경 사항이 있습니다.
      </Typography>
      <Box flex={1} />
      <Button size="small" onClick={onReset} disabled={saving}>되돌리기</Button>
      <Button size="small" variant="contained" onClick={onSave} disabled={saving}>
        {saving ? "저장 중..." : "저장"}
      </Button>
    </Box>
  );
}

export default function OperationsBasicHours() {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [innerTab, setInnerTab] = useState(0);
  const [selectedDay, setSelectedDay] = useState("monday");
  const [lessonConfig, setLessonConfig] = useState(null);
  const [lessonSnapshot, setLessonSnapshot] = useState(null);
  const [practiceConfig, setPracticeConfig] = useState(null);
  const [practiceSnapshot, setPracticeSnapshot] = useState(null);
  const [savingLesson, setSavingLesson] = useState(false);
  const [savingPractice, setSavingPractice] = useState(false);
  // 연습실 그리드 드래그 페인팅: { target: true(차단) | false(해제) }
  const [paint, setPaint] = useState(null);
  // 레슨 시간표 블록 가장자리 드래그: { group, edge, startY, origMin }
  const [drag, setDrag] = useState(null);
  // 수동 축 시작(이전 시간 보기로 확장). 실제 축 시작은 설정값에 따라 더 내려갈 수 있다.
  const [axisStart, setAxisStart] = useState(AXIS_DEFAULT_START);
  const axisStartRef = useRef(AXIS_DEFAULT_START);

  const fetchAll = useCallback(async () => {
    try {
      const [lessonRes, practiceRes] = await Promise.all([
        api.get("/lesson-config"),
        api.get("/practice-config")
      ]);
      const lesson = {};
      LESSON_DAYS.forEach(([key]) => {
        lesson[key] = {
          start: hhmm(lessonRes.data[`${key}_start_time`]) || LESSON_DAY_DEFAULTS[key][0],
          end: hhmm(lessonRes.data[`${key}_end_time`]) || LESSON_DAY_DEFAULTS[key][1]
        };
      });
      setLessonConfig(lesson);
      setLessonSnapshot(JSON.parse(JSON.stringify(lesson)));

      const p = practiceRes.data;
      const rooms = [...(p.lesson_restricted_rooms || []), ...(p.practice_rooms || [])];
      const days = {};
      DAYS.forEach(([key]) => {
        days[key] = {
          start: hhmm(p[`${key}_start_time`]) || "14:00",
          end: hhmm(p[`${key}_end_time`]) || "22:00",
          unavailable_times: p[`${key}_unavailable_times`] || [],
          rooms_by_time: p[`${key}_unavailable_rooms_by_time`] || {}
        };
      });
      const practice = {
        weekday_booking_start_hour: p.weekday_booking_start_hour ?? 12,
        rooms: rooms.length > 0 ? rooms : DEFAULT_ROOMS,
        days
      };
      setPracticeConfig(practice);
      setPracticeSnapshot(JSON.parse(JSON.stringify(practice)));
    } catch (err) {
      console.error("기본 운영시간 조회 실패:", err);
      toast("설정을 불러오지 못했습니다.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // 연습실 그리드 페인팅 종료
  useEffect(() => {
    const end = () => setPaint(null);
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, []);

  // 레슨 시간표 블록 가장자리 드래그 (30분 스냅, 요일별)
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const deltaSlots = Math.round((e.clientY - drag.startY) / SLOT_H);
      const newMin = drag.origMin + deltaSlots * 30;
      setLessonConfig((prev) => {
        const d = prev[drag.day];
        if (!d) return prev;
        const s = toMin(d.start);
        const en = toMin(d.end);
        if (drag.edge === "start") {
          const clamped = Math.max(axisStartRef.current, Math.min(newMin, en - 30));
          if (clamped === s) return prev;
          return { ...prev, [drag.day]: { ...d, start: minToTime(clamped) } };
        }
        const clamped = Math.min(AXIS_END, Math.max(newMin, s + 30));
        if (clamped === en) return prev;
        return { ...prev, [drag.day]: { ...d, end: minToTime(clamped) } };
      });
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const lessonDirty = lessonConfig && JSON.stringify(lessonConfig) !== JSON.stringify(lessonSnapshot);
  const practiceDirty = practiceConfig && JSON.stringify(practiceConfig) !== JSON.stringify(practiceSnapshot);

  const resetLesson = () => setLessonConfig(JSON.parse(JSON.stringify(lessonSnapshot)));
  const resetPractice = () => setPracticeConfig(JSON.parse(JSON.stringify(practiceSnapshot)));

  const updateDay = (dayKey, patch) => {
    setPracticeConfig((prev) => ({
      ...prev,
      days: { ...prev.days, [dayKey]: { ...prev.days[dayKey], ...patch } }
    }));
  };

  const toggleRowBlock = (dayKey, time) => {
    setPracticeConfig((prev) => {
      const list = prev.days[dayKey].unavailable_times;
      const next = list.includes(time) ? list.filter((t) => t !== time) : [...list, time];
      return { ...prev, days: { ...prev.days, [dayKey]: { ...prev.days[dayKey], unavailable_times: next } } };
    });
  };

  const setCellBlock = (dayKey, time, room, blocked) => {
    setPracticeConfig((prev) => {
      const roomsByTime = prev.days[dayKey].rooms_by_time;
      const current = roomsByTime[time] || [];
      if (blocked === current.includes(room)) return prev;
      const updated = blocked ? [...current, room] : current.filter((r) => r !== room);
      const next = { ...roomsByTime, [time]: updated };
      if (updated.length === 0) delete next[time];
      return { ...prev, days: { ...prev.days, [dayKey]: { ...prev.days[dayKey], rooms_by_time: next } } };
    });
  };

  async function saveLesson() {
    for (const [key, label] of LESSON_DAYS) {
      if (lessonConfig[key].start >= lessonConfig[key].end) {
        toast(`${label}요일: 시작 시간은 종료 시간보다 빨라야 합니다.`, "warning");
        return;
      }
    }
    setSavingLesson(true);
    try {
      const payload = {};
      LESSON_DAYS.forEach(([key]) => {
        payload[`${key}_start_time`] = lessonConfig[key].start;
        payload[`${key}_end_time`] = lessonConfig[key].end;
      });
      await api.put("/lesson-config", payload);
      setLessonSnapshot(JSON.parse(JSON.stringify(lessonConfig)));
      toast("레슨 기본 시간이 저장되었습니다.");
    } catch (err) {
      toast(err?.response?.data?.message || "저장 실패", "error");
    } finally {
      setSavingLesson(false);
    }
  }

  async function savePractice(force = false) {
    for (const [key, label] of DAYS) {
      if (practiceConfig.days[key].start >= practiceConfig.days[key].end) {
        toast(`${label}요일: 시작 시간은 종료 시간보다 빨라야 합니다.`, "warning");
        return;
      }
    }
    setSavingPractice(true);
    try {
      const payload = { weekday_booking_start_hour: practiceConfig.weekday_booking_start_hour, force };
      DAYS.forEach(([key]) => {
        const d = practiceConfig.days[key];
        payload[`${key}_start_time`] = d.start;
        payload[`${key}_end_time`] = d.end;
        payload[`${key}_unavailable_times`] = d.unavailable_times;
        payload[`${key}_unavailable_rooms_by_time`] = d.rooms_by_time;
      });
      await api.put("/practice-config", payload);
      setPracticeSnapshot(JSON.parse(JSON.stringify(practiceConfig)));
      toast("연습실 설정이 저장되었습니다.");
    } catch (err) {
      const conflicts = err?.response?.data?.conflicts;
      if (err?.response?.status === 409 && Array.isArray(conflicts)) {
        const lines = conflicts.slice(0, 10).map((c) =>
          `• ${c.user_name} — ${c.date} ${hhmm(c.start_time)}~${hhmm(c.end_time)} ${c.room_name} (${c.reason})`
        );
        if (conflicts.length > 10) lines.push(`• 외 ${conflicts.length - 10}건`);
        const proceed = await confirm({
          title: "예약 충돌 경고",
          severity: "warning",
          message:
            `새 설정과 충돌하는 예약이 ${conflicts.length}건 있습니다.\n\n` +
            lines.join("\n") +
            `\n\n이대로 저장하면 해당 예약은 그대로 유지되지만 새 설정 밖에 남습니다.\n계속하시겠습니까?`,
          confirmText: "무시하고 저장",
          color: "warning"
        });
        if (proceed) {
          await savePractice(true);
          return;
        }
      } else {
        toast(err?.response?.data?.message || "저장 실패", "error");
      }
    } finally {
      setSavingPractice(false);
    }
  }

  if (!lessonConfig || !practiceConfig) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const day = practiceConfig.days[selectedDay];
  const dayLabel = DAYS.find(([k]) => k === selectedDay)?.[1];
  const daySlots = generateTimeSlots(day.start, day.end);

  const cellColors = {
    open: { bg: "transparent", hover: darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
    roomBlocked: { bg: darkMode ? "rgba(244, 67, 54, 0.38)" : "#f6b3ad" },
    rowBlocked: { bg: darkMode ? "rgba(244, 67, 54, 0.6)" : "#e57373" }
  };

  const blockColors = {
    weekday: darkMode ? "#4f8fd8" : "#1976d2",
    saturday: darkMode ? "#66a06a" : "#2e7d32"
  };
  const lessonBlockColor = (dayKey) => (dayKey === "saturday" ? blockColors.saturday : blockColors.weekday);

  // 레슨 주간 시간표 컬럼: [라벨, 요일 키], 일요일은 휴무 고정
  const LESSON_COLS = [...LESSON_DAYS.map(([key, label]) => [label, key]), ["일", null]];

  const gridLineCss = {
    backgroundImage: `repeating-linear-gradient(to bottom, ${darkMode ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)"} 0 1px, transparent 1px ${SLOT_H * 2}px)`
  };

  // 유효 축 시작: 수동 확장값과 레슨 시작 시각 중 이른 쪽 (정시 스냅)
  const lessonStarts = LESSON_DAYS.map(([key]) => toMin(lessonConfig[key].start));
  const dataStart = lessonStarts.length > 0 ? Math.floor(Math.min(...lessonStarts) / 60) * 60 : AXIS_DEFAULT_START;
  const effStart = Math.max(AXIS_MIN, Math.min(axisStart, dataStart));
  axisStartRef.current = effStart;
  const yOf = (min) => ((min - effStart) / 30) * SLOT_H;
  const axisHeight = ((AXIS_END - effStart) / 30) * SLOT_H;

  const renderLessonBlock = (dayKey) => {
    const d = lessonConfig[dayKey];
    if (!d) return null;
    const start = toMin(d.start);
    const end = toMin(d.end);
    const top = yOf(Math.max(start, effStart));
    const height = Math.max(yOf(Math.min(end, AXIS_END)) - top, SLOT_H);
    return (
      <Box sx={{
        position: "absolute", left: 3, right: 3, top, height,
        borderRadius: 1, bgcolor: lessonBlockColor(dayKey), color: "#fff",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        boxShadow: drag?.day === dayKey ? 3 : 0
      }}>
        <Box
          onMouseDown={(e) => { e.preventDefault(); setDrag({ day: dayKey, edge: "start", startY: e.clientY, origMin: start }); }}
          sx={{ height: 10, cursor: "ns-resize", display: "flex", justifyContent: "center", alignItems: "center",
            "&:hover > span": { opacity: 1 } }}
        >
          <Box component="span" sx={{ width: 22, height: 3, borderRadius: 2, bgcolor: "rgba(255,255,255,0.75)", opacity: 0.45 }} />
        </Box>
        <Typography variant="caption" textAlign="center" fontWeight={600} sx={{ fontSize: "0.85rem", lineHeight: 1.3 }}>
          {d.start}<br />~<br />{d.end}
        </Typography>
        <Box
          onMouseDown={(e) => { e.preventDefault(); setDrag({ day: dayKey, edge: "end", startY: e.clientY, origMin: end }); }}
          sx={{ height: 10, cursor: "ns-resize", display: "flex", justifyContent: "center", alignItems: "center",
            "&:hover > span": { opacity: 1 } }}
        >
          <Box component="span" sx={{ width: 22, height: 3, borderRadius: 2, bgcolor: "rgba(255,255,255,0.75)", opacity: 0.45 }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 42, flexShrink: 0, px: 2, "& .MuiTab-root": { minHeight: 42, py: 0 } }}>
        <Tab label="레슨" />
        <Tab label="연습실" />
      </Tabs>

      {/* 레슨 하위 탭: 좌측 정밀 입력 + 우측 주간 시간표 (블록 드래그) */}
      {innerTab === 0 && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ width: 300, flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={0.3}>레슨 기본 시간</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                시간표의 블록 위/아래를 드래그하거나 아래에서 직접 선택하세요.
              </Typography>

              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                {LESSON_DAYS.map(([key, label]) => (
                  <Stack key={key} direction="row" alignItems="center" spacing={1} mb={1.55}>
                    <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: lessonBlockColor(key), flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={600} sx={{ width: 22 }}>{label}</Typography>
                    <TimeSelect label="시작" value={lessonConfig[key].start}
                      onChange={(v) => setLessonConfig({ ...lessonConfig, [key]: { ...lessonConfig[key], start: v } })} />
                    <TimeSelect label="종료" value={lessonConfig[key].end}
                      onChange={(v) => setLessonConfig({ ...lessonConfig, [key]: { ...lessonConfig[key], end: v } })} />
                  </Stack>
                ))}
                <Stack direction="row" alignItems="center" spacing={1} mb={1.55}>
                  <Box sx={{ width: 9, height: 9, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ width: 22 }} color="error.main">일</Typography>
                  <Typography variant="caption" color="text.disabled">휴무 (레슨 없음)</Typography>
                </Stack>
              </Paper>

              <Alert severity="warning" sx={{ py: 0.3, "& .MuiAlert-message": { fontSize: "0.78rem" } }}>
                저장하면 회원 예약 화면의 예약 가능 시간에 바로 반영됩니다.
              </Alert>
            </Box>

            {/* 주간 시간표 */}
            <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 1.5, userSelect: "none" }}>
              {/* 시간 축 확장 (위) */}
              {effStart > AXIS_MIN && (
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    size="small"
                    startIcon={<KeyboardArrowUp sx={{ fontSize: 14 }} />}
                    onClick={() => setAxisStart(Math.max(AXIS_MIN, effStart - 60))}
                    sx={{ fontSize: "0.75rem", py: 0, color: "text.secondary" }}
                  >
                    이전 시간 보기 ({minToTime(Math.max(AXIS_MIN, effStart - 60))}~)
                  </Button>
                </Box>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: `52px repeat(7, 1fr)`, gap: 0 }}>
                <Box />
                {LESSON_COLS.map(([label]) => (
                  <Typography key={label} variant="caption" textAlign="center" fontWeight={600}
                    color={label === "일" ? "error.main" : label === "토" ? "primary.main" : "text.secondary"}
                    sx={{ pb: 0.5, fontSize: "0.85rem" }}>
                    {label}
                  </Typography>
                ))}

                {/* 시간축 라벨 */}
                <Box sx={{ position: "relative", height: axisHeight }}>
                  {Array.from({ length: Math.floor((AXIS_END - effStart) / 60) + 1 }, (_, i) => {
                    const m = effStart + i * 60;
                    if (m > AXIS_END) return null;
                    return (
                      <Typography key={m} variant="caption" color="text.disabled"
                        sx={{ position: "absolute", top: yOf(m) - 9, right: 6, fontSize: "0.8rem" }}>
                        {minToTime(m)}
                      </Typography>
                    );
                  })}
                </Box>

                {/* 요일 컬럼 */}
                {LESSON_COLS.map(([label, group]) => (
                  <Box key={label} sx={{
                    position: "relative", height: axisHeight,
                    borderLeft: "1px solid", borderColor: "divider",
                    ...(group ? gridLineCss : {}),
                    bgcolor: group ? "transparent" : (darkMode ? "rgba(255,255,255,0.03)" : "grey.50")
                  }}>
                    {group ? renderLessonBlock(group) : (
                      <Typography variant="caption" color="text.disabled"
                        sx={{ position: "absolute", top: "50%", left: 0, right: 0, textAlign: "center", transform: "translateY(-50%)" }}>
                        휴무
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Stack>
          <SaveBar dirty={lessonDirty} saving={savingLesson} onSave={saveLesson} onReset={resetLesson} />
        </Box>
      )}

      {/* 연습실 하위 탭 */}
      {innerTab === 1 && (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>주간 운영 현황</Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.8, mb: 2 }}>
            {DAYS.map(([key, label]) => {
              const d = practiceConfig.days[key];
              const active = selectedDay === key;
              return (
                <Box
                  key={key}
                  onClick={() => setSelectedDay(key)}
                  sx={{
                    py: 1, borderRadius: 1.5, cursor: "pointer", textAlign: "center",
                    border: "2px solid",
                    borderColor: active ? "primary.main" : "divider",
                    bgcolor: active ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: active ? "action.selected" : "action.hover" }
                  }}
                >
                  <Typography variant="body2" fontWeight={active ? 700 : 600}
                    color={key === "sunday" ? "error.main" : key === "saturday" ? "primary.main" : "text.primary"}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.8rem" }}>
                    {d.start}~{d.end}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* 예약 정책 — 운영시간(언제 여는가)과 구분되는 별도 카드 */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>평일 당일 예약 오픈</Typography>
              <Typography variant="caption" color="text.secondary">
                평일 연습실은 먼 날짜 선점을 막기 위해 당일에만 예약할 수 있으며, 매일 이 시각에 예약이 열립니다.
              </Typography>
            </Box>
            <FormControl size="small" sx={{ width: 120, flexShrink: 0 }}>
              <InputLabel>오픈 시각</InputLabel>
              <Select
                value={practiceConfig.weekday_booking_start_hour}
                label="오픈 시각"
                onChange={(e) => setPracticeConfig({ ...practiceConfig, weekday_booking_start_hour: e.target.value })}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>{h}시</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Typography variant="subtitle2" fontWeight={600}>{dayLabel}요일 운영 시간</Typography>
            <TimeSelect label="시작" value={day.start} onChange={(v) => updateDay(selectedDay, { start: v })} />
            <TimeSelect label="종료" value={day.end} onChange={(v) => updateDay(selectedDay, { end: v })} />
          </Stack>

          <Stack direction="row" alignItems="center" mb={1}>
            <Typography variant="caption" color="text.secondary">
              칸을 클릭하거나 드래그해서 예약 불가로 칠하세요. 시간을 클릭하면 그 시간 전체(모든 연습실)가 차단됩니다.
            </Typography>
            <Box flex={1} />
            <Stack direction="row" spacing={2} alignItems="center">
              {[
                { label: "예약 가능", sx: { border: "1px solid", borderColor: "divider", bgcolor: "transparent" } },
                { label: "방 차단", sx: { bgcolor: cellColors.roomBlocked.bg } },
                { label: "전체 차단", sx: { bgcolor: cellColors.rowBlocked.bg } }
              ].map((item) => (
                <Stack key={item.label} direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, ...item.sx }} />
                  <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Box sx={{
            display: "grid",
            gridTemplateColumns: `72px repeat(${practiceConfig.rooms.length}, 1fr)`,
            gap: "3px",
            userSelect: "none",
            mb: 1
          }}>
            <Box />
            {practiceConfig.rooms.map((room) => (
              <Typography key={room} variant="caption" textAlign="center" fontWeight={600} color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                {room}
              </Typography>
            ))}

            {daySlots.map((time) => {
              const rowBlocked = day.unavailable_times.includes(time);
              return (
                <React.Fragment key={time}>
                  <Tooltip title={rowBlocked ? "전체 차단 해제" : "이 시간 전체 차단"} placement="left" arrow>
                    <Box
                      onClick={() => toggleRowBlock(selectedDay, time)}
                      sx={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        height: 34, borderRadius: 1, cursor: "pointer",
                        fontSize: "0.85rem", fontWeight: rowBlocked ? 700 : 500,
                        color: rowBlocked ? "#fff" : "text.secondary",
                        bgcolor: rowBlocked ? cellColors.rowBlocked.bg : "action.hover",
                        "&:hover": { opacity: 0.85 }
                      }}
                    >
                      {time}
                    </Box>
                  </Tooltip>
                  {practiceConfig.rooms.map((room) => {
                    const cellBlocked = (day.rooms_by_time[time] || []).includes(room);
                    if (rowBlocked) {
                      return (
                        <Box key={room} sx={{ height: 34, borderRadius: 1, bgcolor: cellColors.rowBlocked.bg, opacity: 0.55 }} />
                      );
                    }
                    return (
                      <Box
                        key={room}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setPaint({ target: !cellBlocked });
                          setCellBlock(selectedDay, time, room, !cellBlocked);
                        }}
                        onMouseEnter={() => {
                          if (paint) setCellBlock(selectedDay, time, room, paint.target);
                        }}
                        sx={{
                          height: 34, borderRadius: 1, cursor: "pointer",
                          border: "1px solid",
                          borderColor: cellBlocked ? "transparent" : "divider",
                          bgcolor: cellBlocked ? cellColors.roomBlocked.bg : cellColors.open.bg,
                          "&:hover": { bgcolor: cellBlocked ? cellColors.roomBlocked.bg : cellColors.open.hover }
                        }}
                      />
                    );
                  })}
                </React.Fragment>
              );
            })}
          </Box>

          <SaveBar dirty={practiceDirty} saving={savingPractice} onSave={() => savePractice()} onReset={resetPractice} />
        </Box>
      )}
    </Box>
  );
}
